import React from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon, ArrowTrendingUpIcon, SparklesIcon } from '@heroicons/react/24/outline';
import type { Highlight } from '../lib/rollup';
import { scoreColor } from '../lib/score';
// Shared with Top10List so the "cap at 2 + K more" merged-team-name display
// can't drift between the two surfaces — this card is narrower, which is the
// whole reason the cap exists.
import { formatTeamLabel } from './Top10List';

const HIGHLIGHT_ICONS: Record<Highlight['kind'], typeof TrophyIcon> = {
  top: TrophyIcon,
  improved: ArrowTrendingUpIcon,
  adopted: SparklesIcon,
};

/** A single positive-only callout card for the rollup Highlights band. */
export function HighlightCard({ highlight }: { highlight: Highlight }): React.JSX.Element {
  const Icon = HIGHLIGHT_ICONS[highlight.kind];
  const team = highlight.teamName ? formatTeamLabel(highlight.teamName) : null;
  return (
    <div className="card p-md" data-testid={`highlight-${highlight.kind}`}>
      <div className="flex items-center gap-sm mb-sm">
        <Icon aria-hidden="true" style={{ width: 20, height: 20, color: 'var(--genai-sem-accent)' }} />
        <span className="text-xs text-muted" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {highlight.label}
        </span>
      </div>
      <p className="font-semibold text-base leading-tight" style={{ marginBottom: 'var(--genai-space-3xs)' }}>
        {highlight.href ? (
          <Link
            to={highlight.href}
            data-testid={`highlight-link-${highlight.kind}`}
            style={{ color: 'inherit' }}
          >
            {highlight.itemName}
          </Link>
        ) : (
          highlight.itemName
        )}
      </p>
      {team && <p className="text-xs text-muted mb-sm" title={team.title}>{team.text}</p>}
      <div className="flex items-center gap-sm">
        <span className="text-2xl font-bold leading-tight" style={{ color: scoreColor(highlight.score) }}>
          {highlight.score}
        </span>
        <span className="text-xs text-secondary">{highlight.detail}</span>
      </div>
    </div>
  );
}
